import * as React from 'react';
import cn from 'classnames';
import { IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';

import { ArrowRightCircle, UserCircle } from 'src/common/icons';
import { Portal } from 'src/common/Portal';
import useLogout from 'src/hooks/useLogout';
import { TSubscription } from 'src/types/TSubscription';
import { NavItem } from '../navItems';
import messages from './messages';
import styles from './AccountMenu.module.scss';

const ROLE_MESSAGE: Record<string, keyof typeof messages> = {
  ADMIN: 'roleAdmin',
  MANAGER: 'roleManager',
  CREATOR: 'roleCreator',
  USER: 'roleUser',
};

/** Up to two initials from a display name ("Ada Lovelace" -> "AL"). An email
 *  or a single word gives one letter. Empty when there is nothing usable, so
 *  the caller falls back to the generic person icon. */
export function initialsFor(name: string): string {
  const isEmail = name.includes('@');
  const words = name.split('@')[0].split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1 || isEmail) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** The secondary line under the name: the plan name when the subscription is
 *  readable (admins), otherwise the role. Plan only — seats were dropped
 *  because the line is truncated at sidebar width and the Subscription page
 *  already shows them. */
export function accountContextLine(
  intl: IntlShape,
  role: string,
  subscription?: TSubscription,
): string {
  const planName = subscription?.details?.name;
  if (planName) return planName;
  const roleKey = ROLE_MESSAGE[role];
  return roleKey ? intl.formatMessage(messages[roleKey]) : '';
}

type Placement = { left: number; bottom: number; width?: number };

/** Popover position, in viewport coordinates. Expanded, it sits directly
 *  above the trigger at the trigger's width; on the collapsed rail there is
 *  no room above, so it opens to the right, bottom-aligned with the trigger. */
function placementFor(trigger: HTMLElement, collapsed: boolean): Placement {
  const rect = trigger.getBoundingClientRect();
  if (collapsed) {
    return { left: rect.right + 8, bottom: window.innerHeight - rect.bottom };
  }
  return { left: rect.left, bottom: window.innerHeight - rect.top + 6, width: rect.width };
}

export interface AccountMenuProps {
  /** Organization items (Teams & Users, Subscription), already gated by the
   *  sidebar with the same `NavGateCtx` as the nav list. */
  orgItems: NavItem[];
  subscription?: TSubscription;
  collapsed: boolean;
  /** False while the mobile drawer is closed: the trigger is off-screen, so
   *  an open menu must close with it. */
  visible: boolean;
  /** Called after any navigation from the menu (closes the mobile drawer). */
  onNavigate: () => void;
}

export const AccountMenu: React.FC<AccountMenuProps> = ({
  orgItems,
  subscription,
  collapsed,
  visible,
  onNavigate,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const auth = useAuthUser();
  const logout = useLogout();

  const user = auth();
  const username = user?.username || '';
  const email = user?.userEmail || '';
  const role = user?.role || '';
  const displayName = username || email;
  const initials = initialsFor(displayName);
  const roleKey = ROLE_MESSAGE[role];
  const roleLabel = roleKey ? formatMessage(messages[roleKey]) : '';
  const contextLine = accountContextLine(intl, role, subscription);

  const [open, setOpen] = React.useState(false);
  // Which item takes focus when the menu opens: ArrowUp on the trigger lands
  // on the last item (WAI-ARIA menu button pattern), everything else on the
  // first.
  const [focusOnOpen, setFocusOnOpen] = React.useState<'first' | 'last'>('first');
  const [placement, setPlacement] = React.useState<Placement | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuId = 'app-account-menu';

  const close = React.useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Placement is measured here rather than in an effect so the menu's first
  // paint is already in place: a `visibility: hidden` first frame would make
  // the focus-on-open below a silent no-op in a real browser.
  const openMenu = (focus: 'first' | 'last') => {
    if (!triggerRef.current) return;
    setFocusOnOpen(focus);
    setPlacement(placementFor(triggerRef.current, collapsed));
    setOpen(true);
  };

  const menuItems = (): HTMLElement[] =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  // Close when the rail changes width or the mobile drawer slides away —
  // either moves the trigger out from under the popover.
  React.useEffect(() => {
    setOpen(false);
  }, [collapsed, visible]);

  // Follow the trigger while the menu is open: a window resize, a sidebar
  // scroll, or the end of a transition — the mobile drawer slides in with a
  // transform, so a menu opened mid-slide would otherwise stay where the
  // drawer was (off-screen), and a transform fires neither resize nor scroll.
  React.useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) setPlacement(placementFor(triggerRef.current, collapsed));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('transitionend', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('transitionend', update, true);
    };
  }, [open, collapsed]);

  React.useEffect(() => {
    if (!open) return;
    const items = menuItems();
    (focusOnOpen === 'last' ? items[items.length - 1] : items[0])?.focus();
    // Only on open: re-placement while open must not steal focus back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Outside click. The menu is portalled to <body>, so "inside" is the
  // trigger OR the menu.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(e.key === 'ArrowUp' ? 'last' : 'first');
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const items = menuItems();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(items.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        // The menu is portalled to the end of <body>, so letting Tab through
        // would drop focus outside the app. Close and go back to the trigger.
        e.preventDefault();
        close(true);
        break;
      default:
    }
  };

  const onItemNavigate = () => {
    close(false);
    onNavigate();
  };

  const avatar = (
    <span className={styles.avatar} aria-hidden>
      {initials || <UserCircle />}
    </span>
  );

  const triggerLabel = displayName
    ? formatMessage(messages.trigger, { name: displayName })
    : formatMessage(messages.triggerNoName);

  return (
    <div
      className={cn(styles.wrap, { [styles.wrapCollapsed]: collapsed })}
      ref={wrapRef}
      data-test="user-dropdown"
    >
      <button
        type="button"
        ref={triggerRef}
        className={cn(styles.trigger, { [styles.triggerOpen]: open })}
        onClick={() => (open ? close(false) : openMenu('first'))}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        title={collapsed ? triggerLabel : undefined}
        data-test="account-menu-trigger"
      >
        {avatar}
        {!collapsed && (
          <span className={styles.triggerText}>
            <span className={styles.triggerName}>{displayName || ' '}</span>
            {contextLine && <span className={styles.triggerContext}>{contextLine}</span>}
          </span>
        )}
      </button>

      {open && placement && (
        <Portal>
          <div
            id={menuId}
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label={triggerLabel}
            onKeyDown={onMenuKeyDown}
            style={{ left: placement.left, bottom: placement.bottom, width: placement.width }}
            data-test="account-menu"
          >
            <div className={styles.header}>
              {avatar}
              <div className={styles.headerText}>
                <div className={styles.headerName}>{displayName || ' '}</div>
                {email && email !== displayName && (
                  <div className={styles.headerEmail}>{email}</div>
                )}
                {roleLabel && <span className={styles.badge}>{roleLabel}</span>}
              </div>
            </div>

            <Link
              to="/me"
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              onClick={onItemNavigate}
              data-test="user-menu-profile"
            >
              <UserCircle />
              <span>{formatMessage(messages.profile)}</span>
            </Link>

            {orgItems.length > 0 && (
              <div role="group" aria-labelledby="app-account-menu-org">
                <div className={styles.groupLabel} id="app-account-menu-org">
                  {formatMessage(messages.organization)}
                </div>
                {orgItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.to}
                      role="menuitem"
                      tabIndex={-1}
                      className={styles.item}
                      onClick={onItemNavigate}
                      data-test={item.dataTest}
                    >
                      <Icon />
                      <span>{formatMessage(item.label)}</span>
                    </Link>
                  );
                })}
              </div>
            )}

            <div className={styles.divider} role="separator" />

            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              onClick={() => {
                close(false);
                logout();
              }}
              data-test="user-menu-logout"
            >
              <ArrowRightCircle />
              <span>{formatMessage(messages.logout)}</span>
            </button>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default AccountMenu;
